// NewKBBoard.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Flex, Button, Text, Spinner, useToast, Heading, Modal, ModalOverlay, ModalContent } from '@chakra-ui/react';
import ReactConfetti from 'react-confetti';
// Removed DragDropContext import as it's now in NewKBBoard-BoardLayout
import { useNewKBLead } from '../context/NewKBLeadContext';
import { useSocket } from '../../../../contexts/SocketContext';
import NewKBLeadModal from './NewKBLeadModal';
import NewKBFilters from './NewKBFilters';
import { STAGE_ORDER } from '../utils/NewKBConstants';
import api from '../../../../services/api';
// Removed AddIcon and Heading imports if no longer used directly here
import moment from 'moment';
import { UserProvider } from '../context/UserContext';

// Import the new modular components/functions
import { filterLeads } from './NewKBBoard-filtering';
import NewKBBoardHeader from './NewKBBoard-Header';
import NewKBBoardLayout from './NewKBBoard-BoardLayout';

// Import AI extraction monitor
import AIExtractionMonitor from './ai-extraction/AIExtractionMonitor';


const NewKBBoard = () => {
  const toast = useToast();
  const { socket, emitWithSourceId } = useSocket();
  const {
    stages,
    isLoading, // Renamed for clarity, this is the general loading state
    hasMore,
    isLoadingAllPages,
    loadMoreLeads, // Keep if pagination is used elsewhere, otherwise remove
    loadAllLeads,
    leads,
    fetchLeads,
    updateLeadLocally
  } = useNewKBLead();

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [callFilter, setCallFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState([]);
  const [sourceFilter, setSourceFilter] = useState([]); // New source filter state
  const [assigneeFilter, setAssigneeFilter] = useState(''); // Keep state if filter UI needs it
  const [leadOwnerFilter, setLeadOwnerFilter] = useState(''); // New lead owner filter state
  const [missedFollowUpFilter, setMissedFollowUpFilter] = useState(false);
  const [todayFollowUpFilter, setTodayFollowUpFilter] = useState(false);

  // Date filter states
  const [dateFilter, setDateFilter] = useState({
    startDate: null,
    endDate: null,
    focusedInput: null
  });
  const [appliedDateFilter, setAppliedDateFilter] = useState({
    startDate: null,
    endDate: null
  });
  const [datePreset, setDatePreset] = useState('');
  const [focusedInput, setFocusedInput] = useState(null);

  // Users for assigning leads (and potentially assignee filter)
  const [users, setUsers] = useState([]);

  // State for modal and drag context readiness
  const [isAddLeadModalOpen, setIsAddLeadModalOpen] = useState(false);
  const [isDragContextReady, setIsDragContextReady] = useState(false);
  
  // Confetti celebration state
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0
  });

  // Load users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('/api/users');
        if (response.data && response.data.users) {
          console.log('[NewKBBoard] Fetched users:', response.data.users.length);
          setUsers(response.data.users);
        } else {
          console.warn('[NewKBBoard] No users data in API response');
        }
      } catch (error) {
        console.error('[NewKBBoard] Error fetching users:', error);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    console.log('[NewKBBoard] Users state updated:', users.length);
  }, [users]);

  // Check if all required stages exist
  const areAllStagesLoaded = useMemo(() =>
    stages.length > 0 && STAGE_ORDER.every(stageId => stages.some(stage => stage._id === stageId)),
    [stages]
  );

  // Calculate filtered leads using the imported function
  const filteredLeads = useMemo(() => {
    return filterLeads(leads, {
      searchQuery,
      callFilter,
      tagFilter,
      sourceFilter, // Pass the new source filter
      assigneeFilter, // Pass even if filter logic removed, UI might need it
      leadOwnerFilter, // Pass the new lead owner filter
      missedFollowUpFilter,
      todayFollowUpFilter,
      appliedDateFilter
    });
  }, [
    leads,
    searchQuery,
    callFilter,
    tagFilter,
    sourceFilter, // Add to dependency array
    assigneeFilter,
    leadOwnerFilter, // Add to dependency array
    missedFollowUpFilter,
    todayFollowUpFilter,
    appliedDateFilter
  ]);

  // Handle call range changes (passed down to filters)
  const handleCallRangeChange = useCallback((min, max) => {
    console.log('[NewKBBoard] Call range changed:', { min, max });
    // Update state or URL params if needed for custom range filter
  }, []);

  // Clear all filters handler
  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setCallFilter('all');
    setTagFilter([]);
    setSourceFilter([]); // Reset source filter
    setAssigneeFilter('');
    setLeadOwnerFilter(''); // Reset lead owner filter
    setMissedFollowUpFilter(false);
    setTodayFollowUpFilter(false);
    setAppliedDateFilter({ startDate: null, endDate: null });
    setDateFilter({ startDate: null, endDate: null, focusedInput: null }); // Reset date picker state
    setDatePreset(''); // Reset date preset
  }, []);

   // Load All Leads handler
   const handleLoadAllLeads = useCallback(() => {
    if (socket && socket.connected) {
      emitWithSourceId('lead:updated', {
        _systemEvent: 'initialLoad',
        timestamp: new Date().toISOString()
      });
    }
    loadAllLeads();
  }, [socket, emitWithSourceId, loadAllLeads]);


  // Ensure drag context is ready only after stages are loaded
  useEffect(() => {
    if (stages.length > 0 && areAllStagesLoaded) {
      const timer = setTimeout(() => {
        setIsDragContextReady(true);
        console.log('[NewKBBoard] Drag context ready with stages:', stages.length);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setIsDragContextReady(false);
      console.log('[NewKBBoard] Drag context not ready, waiting for stages');
    }
  }, [stages, areAllStagesLoaded]);

  // Update window size for confetti
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-hide confetti after some time
  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 8000); // Show confetti for 8 seconds
      return () => clearTimeout(timer);
    }
  }, [showConfetti]);

  // Drag End Handler - Remains here as it interacts heavily with state and context
  const handleDragEnd = useCallback(async (result) => {
    console.log('[NewKBBoard] Drag end result:', result);
    const { source, destination, draggableId } = result;

    if (!draggableId.startsWith('SUM-')) {
        console.error('[NewKBBoard] Invalid draggableId format', { draggableId });
        return;
    }
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    if (source.droppableId !== destination.droppableId) {
      try {
        const lead = leads.find(l => l.leadId === draggableId);
        if (!lead) {
          console.error('[NewKBBoard] Lead not found:', draggableId);
          toast({ title: 'Error', description: 'Lead data not found. Please refresh.', status: 'error', duration: 3000, isClosable: true });
          return;
        }

        const newStageId = destination.droppableId;
        const leadId = lead.leadId || lead._id; // Use business ID primarily
        const newStage = stages.find(s => s._id === newStageId);
        const stageName = newStage ? newStage.name : 'new stage';

        // Optimistic local update (can be done via updateLeadLocally if preferred)
        // updateLeadLocally({ ...lead, stage: newStageId });

        if (socket?.connected) {
          socket.emit('stage:change', { leadId: draggableId, newStage: newStageId });
        }

        const response = await api.put(`/api/leads/${leadId}`, { stage: newStageId });

        if (response.data) {
          const updatedLead = { ...response.data, leadId: response.data.leadId || draggableId }; // Ensure leadId persists
          updateLeadLocally(updatedLead); // Update with server confirmed data

          if (socket?.connected) {
            socket.emit('lead:stage-updated', {
              leadId: draggableId,
              stage: newStageId,
              stageName,
              previousStage: source.droppableId,
              timestamp: new Date().toISOString()
            });
            console.log('[NewKBBoard] Emitted lead:stage-updated via socket');
          }

          // Show confetti celebration if lead was moved to Closed Won
          if (newStageId === 'stage_closed_won') {
            console.log('[NewKBBoard] Lead moved to Closed Won, showing confetti celebration!');
            setShowConfetti(true);
          } else {
            toast({
              title: 'Stage updated',
              description: `Lead moved to ${stageName}`,
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          }
        } else {
             throw new Error("No data received in response"); // Trigger catch block
        }

      } catch (error) {
        console.error('Error updating lead stage:', error);
        let errorMessage = 'Failed to update stage';
        let requirementsList = [];

        if (error.response?.data) {
            if (error.response.data.error === 'REQUIREMENTS_NOT_MET' && error.response.data.requirements) {
                requirementsList = error.response.data.requirements;
                errorMessage = 'This lead cannot be moved to this stage yet';
            } else if (error.response.data.message) {
                errorMessage = error.response.data.message;
            }
        }

        toast({
          title: 'Cannot update stage',
          description: (
            <Box>
              <Text>{errorMessage}</Text>
              {requirementsList.length > 0 && (
                <Box mt={2}>
                  <Text fontWeight="bold">Missing requirements:</Text>
                  <Box ml={2}>
                    {requirementsList.map((req, idx) => (<Text key={idx}>• {req}</Text>))}
                  </Box>
                </Box>
              )}
            </Box>
          ),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });

        // Revert optimistic update or refetch to correct UI state
         await fetchLeads(); // Refetch to ensure consistency after error
      }
    }
  }, [leads, stages, socket, updateLeadLocally, fetchLeads, toast]); // Added dependencies

  // Initial Loading State
  if (isLoading && stages.length === 0) {
    return (
      <Flex justify="center" align="center" h="50vh">
        <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
      </Flex>
    );
  }

  // Main Render
  return (
    <UserProvider users={users}>
      {/* {console.log('[NewKBBoard] Rendering UserProvider with users:', users.length)} */}
      <Box width="100%" p={0} mt={-2}>

        {/* Loading indicator for loading all pages */}
        {isLoadingAllPages && (
          <Flex justify="center" align="center" width="100%" bg="blue.50" py={2} mb={1}>
            <Spinner size="sm" mr={2} color="blue.500" thickness="3px" />
            <Text color="blue.700" fontSize="md" fontWeight="medium">
              Loading all leads ({leads.length}+ leads loaded so far)...
            </Text>
          </Flex>
        )}

        {/* Filters Section */}
        <Box pt="5px">
          <NewKBFilters
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            callFilter={callFilter}
            setCallFilter={setCallFilter}
            tagFilter={tagFilter}
            setTagFilter={setTagFilter}
            sourceFilter={sourceFilter} // Pass source filter
            setSourceFilter={setSourceFilter} // Pass setter
            assigneeFilter={assigneeFilter}
            setAssigneeFilter={setAssigneeFilter}
            leadOwnerFilter={leadOwnerFilter} // Pass lead owner filter
            setLeadOwnerFilter={setLeadOwnerFilter} // Pass setter
            missedFollowUpFilter={missedFollowUpFilter}
            setMissedFollowUpFilter={setMissedFollowUpFilter}
            todayFollowUpFilter={todayFollowUpFilter}
            setTodayFollowUpFilter={setTodayFollowUpFilter}
            appliedDateFilter={appliedDateFilter}
            setAppliedDateFilter={setAppliedDateFilter}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            datePreset={datePreset}
            setDatePreset={setDatePreset}
            focusedInput={focusedInput}
            setFocusedInput={setFocusedInput}
            users={users}
            handleAddNewLead={() => setIsAddLeadModalOpen(true)}
            onCallRangeChange={handleCallRangeChange}
          />
        </Box>

        {/* Header: Lead Counts and Load All Button */}
        <NewKBBoardHeader
            filteredLeadsCount={filteredLeads.length}
            totalLeadsCount={leads.length}
            isLoadingAllPages={isLoadingAllPages}
            hasMore={hasMore}
            onClearFilters={handleClearFilters}
            onLoadAllLeads={handleLoadAllLeads}
            isLoading={isLoading && hasMore} // Pass loading state for the button
        />

        {/* Board Layout: Columns and Drag Drop Context */}
        <NewKBBoardLayout
          stages={stages}
          filteredLeads={filteredLeads}
          onDragEnd={handleDragEnd}
          areAllStagesLoaded={areAllStagesLoaded}
          isDragContextReady={isDragContextReady}
        />

        {/* Add Lead Modal */}
        <NewKBLeadModal
          isOpen={isAddLeadModalOpen}
          onClose={() => setIsAddLeadModalOpen(false)}
          onLeadAdded={(newLead) => {
            console.log('[NewKBBoard] Lead created:', newLead);
            if (socket?.connected) {
              socket.emit('lead:created', { leadId: newLead._id, lead: newLead });
              console.log('[SOCKET] Emitted lead:created', { leadId: newLead._id });
            }
            fetchLeads(); // Refresh board after adding
          }}
        />
        
        {/* AI Extraction Monitor - Checks for AI extraction results and displays modal */}
        <AIExtractionMonitor />
        
        {/* Confetti Celebration */}
        {showConfetti && (
          <>
            <ReactConfetti
              width={windowSize.width}
              height={windowSize.height}
              recycle={true}
              numberOfPieces={1200}
              gravity={0.1}
              tweenDuration={8000}
              initialVelocityY={-15}
              wind={0.01}
              colors={['#EA4335', '#FBBC05', '#34A853', '#4285F4', '#FFA726', '#9C27B0', '#FF4081', '#7C4DFF', '#64FFDA', '#FFD54F']}
            />
            <Modal isOpen={showConfetti} onClose={() => setShowConfetti(false)} isCentered size="xl">
              <ModalOverlay />
              <ModalContent
                bg="transparent"
                boxShadow="none"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Heading
                  size="2xl"
                  textAlign="center"
                  color="white"
                  textShadow="0 0 10px #4285F4, 0 0 20px #EA4335, 0 0 30px #FBBC05, 0 0 40px #34A853"
                  fontWeight="light"
                  letterSpacing="wider"
                  py={6}
                  px={8}
                  fontFamily="'Quicksand', sans-serif"
                  position="relative"
                  zIndex="tooltip"
                  transform="rotate(-2deg)"
                  animation="1s bounce infinite alternate"
                  sx={{
                    '@keyframes bounce': {
                      '0%': { transform: 'rotate(-2deg) scale(1)' },
                      '100%': { transform: 'rotate(2deg) scale(1.05)' }
                    }
                  }}
                >
                  ✨ Congratulations Team Sumaaroh! 🎉
                </Heading>
              </ModalContent>
            </Modal>
          </>
        )}
      </Box>
    </UserProvider>
  );
};

export default NewKBBoard;